import { useState, useRef, useEffect, type MouseEvent as ReactMouseEvent } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useDataSync } from '../context/DataSyncContext';
import '../styles/layout.css';

const linkClass = ({ isActive }: { isActive: boolean }) =>
  `nav-link${isActive ? ' nav-link-active' : ''}`;

export function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const {
    adminPendingCount,
    myPendingCount,
    openReportCount,
    refreshAllBadges,
    triggerRouteRefresh
  } = useDataSync();

  const [menuOpen, setMenuOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [extMenuOpen, setExtMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const adminMenuRef = useRef<HTMLDivElement>(null);
  const extMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    setMenuOpen(false);
    void logout();
  };

  const handleNavClick =
    (path: string) => (event: ReactMouseEvent<HTMLAnchorElement>) => {
      if (location.pathname === path) {
        event.preventDefault();
        triggerRouteRefresh(path);
        void refreshAllBadges();
      }
    };

  const handleAdminNavClick =
    (path: string) => (event: ReactMouseEvent<HTMLAnchorElement>) => {
      setAdminMenuOpen(false);
      handleNavClick(path)(event);
    };

  const toggleMenu = () => setMenuOpen((prev) => !prev);
  const toggleAdminMenu = () => setAdminMenuOpen((prev) => !prev);

  const totalAdminBadge = adminPendingCount + openReportCount;

  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
      if (adminMenuRef.current && !adminMenuRef.current.contains(event.target as Node)) {
        setAdminMenuOpen(false);
      }
      if (extMenuRef.current && !extMenuRef.current.contains(event.target as Node)) {
        setExtMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [showFaqTooltip, setShowFaqTooltip] = useState(false);

      useEffect(() => {
        const hasVisitedFaq = localStorage.getItem('hasVisitedFaq') === 'true';
        if (!hasVisitedFaq && user) {
          setShowFaqTooltip(true);
        } else {
          setShowFaqTooltip(false);
        }
      }, [user]);

      const handleFaqClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        handleNavClick('/faq')(event);
        setShowFaqTooltip(false);
        localStorage.setItem('hasVisitedFaq', 'true');
      };

  return (
    <header className="navbar">
      <div className="navbar__brand">
        <span className="navbar__brand-name">IO Marketplace</span>
        {import.meta.env.VITE_BUILD_VERSION && (
          <span className="navbar__version">{import.meta.env.VITE_BUILD_VERSION}</span>
        )}
      </div>

      <nav className="navbar__nav">
        {user?.role === 'admin' && (
          <div className="nav-dropdown" ref={adminMenuRef}>
            <button
              className={`nav-link nav-dropdown__trigger${location.pathname.startsWith('/admin') ? ' nav-link-active' : ''}`}
              onClick={toggleAdminMenu}
              type="button"
            >
              <span>Admin</span>
              {totalAdminBadge > 0 && <span className="badge">{totalAdminBadge}</span>}
              <span className="material-icons nav-dropdown__arrow">
                {adminMenuOpen ? 'expand_less' : 'expand_more'}
              </span>
            </button>
            {adminMenuOpen && (
              <div className="nav-dropdown__menu">
                <NavLink
                  to="/admin/dashboard"
                  className={linkClass}
                  onClick={handleAdminNavClick('/admin/dashboard')}
                >
                  Dashboard
                </NavLink>
                <NavLink
                  to="/admin/pending"
                  className={linkClass}
                  onClick={handleAdminNavClick('/admin/pending')}
                >
                  <span>Pending</span>
                  {adminPendingCount > 0 && (
                    <span className="badge">{adminPendingCount}</span>
                  )}
                </NavLink>
                <NavLink
                  to="/admin/reports"
                  className={linkClass}
                  onClick={handleAdminNavClick('/admin/reports')}
                >
                  <span>Reports</span>
                  {openReportCount > 0 && (
                    <span className="badge">{openReportCount}</span>
                  )}
                </NavLink>
                <NavLink
                  to="/admin/feature-flags"
                  className={linkClass}
                  onClick={handleAdminNavClick('/admin/feature-flags')}
                >
                  Feature Flags
                </NavLink>
                <NavLink
                  to="/admin/validator-config"
                  className={linkClass}
                  onClick={handleAdminNavClick('/admin/validator-config')}
                >
                  Validator Config
                </NavLink>
              </div>
            )}
          </div>
        )}
        {user?.role !== 'admin' && user?.featureFlags?.includes('feature-flags-admin') && (
          <NavLink
            to="/admin/feature-flags"
            className={linkClass}
            onClick={handleNavClick('/admin/feature-flags')}
          >
            Feature Flags
          </NavLink>
        )}
        <NavLink to="/" className={linkClass} end onClick={handleNavClick('/')}>
          Marketplace
        </NavLink>
        <NavLink to="/my" className={linkClass} onClick={handleNavClick('/my')}>
          <span>My Entries</span>
          {myPendingCount > 0 && <span className="badge">{myPendingCount}</span>}
        </NavLink>
        <NavLink
          to="/designer"
          className={linkClass}
          onClick={handleNavClick('/designer')}
        >
          Designer
        </NavLink>
        {user?.featureFlags?.includes('ai-designer') && (
          <NavLink
            to="/ai-designer"
            className={linkClass}
            onClick={handleNavClick('/ai-designer')}
          >
            AI Designer
          </NavLink>
        )}
        {(user?.role === 'admin' ||
          user?.featureFlags?.includes('custom-nodes') ||
          user?.featureFlags?.includes('custom-nodes-publish')) && (
          <NavLink
            to="/custom-nodes"
            className={linkClass}
            onClick={handleNavClick('/custom-nodes')}
          >
            Custom Nodes
          </NavLink>
        )}
        <NavLink to="/teams" className={linkClass} onClick={handleNavClick('/teams')}>
          Teams
        </NavLink>
        <NavLink to="/forum" className={linkClass} onClick={handleNavClick('/forum')}>
          Forum
        </NavLink>
        <NavLink
          to="/faq"
          className={({ isActive }) =>
            `${linkClass({ isActive })}${showFaqTooltip ? ' nav-link--nudge' : ''}`
          }
          onClick={handleFaqClick}
        >
          FAQ
          {showFaqTooltip && (
            <>
              <span className="faq-badge">New</span>
              {showFaqTooltip && (
                <span className="faq-tooltip">
                  Check the FAQ to get started!
                </span>
              )}
            </>
          )}
        </NavLink>
        <div className="nav-dropdown" ref={extMenuRef}>
          <button
            className={`nav-link nav-dropdown__trigger${location.pathname.startsWith('/extension') ? ' nav-link-active' : ''}`}
            onClick={() => setExtMenuOpen((prev) => !prev)}
            type="button"
          >
            <span>Extension</span>
            <span className="material-icons nav-dropdown__arrow">
              {extMenuOpen ? 'expand_less' : 'expand_more'}
            </span>
          </button>
          {extMenuOpen && (
            <div className="nav-dropdown__menu">
              <NavLink
                to="/extension/changelog"
                className={linkClass}
                onClick={() => setExtMenuOpen(false)}
              >
                Changelog
              </NavLink>
              <a
                href="https://chromewebstore.google.com/detail/io-inspector/kkbgajpjbbhlfjjkfcfoedmadoeoebnk"
                className="nav-link"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setExtMenuOpen(false)}
              >
                Chrome Web Store
                <span className="material-icons nav-link__external">open_in_new</span>
              </a>
            </div>
          )}
        </div>
      </nav>

      <div className="navbar__actions" ref={menuRef}>
        <div className="navbar__user-menu">
          <button className="navbar__user-button" onClick={toggleMenu}>
            <span className="navbar__user-icon material-icons">account_circle</span>
            <span className="navbar__user-info">
              <span className="navbar__user-name">{user?.name}</span>
              <span className="navbar__user-role">{user?.role}</span>
            </span>
            <span className="navbar__arrow material-icons">
              {menuOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
            </span>
          </button>

          {menuOpen && (
            <div className="navbar__dropdown">
              <div className="navbar__dropdown-header">
                <div className="navbar__dropdown-name">{user?.name}</div>
                <div className="navbar__dropdown-role">{user?.role}</div>
              </div>

              <Link
                to="/account/preferences"
                className="navbar__dropdown-item"
                onClick={() => setMenuOpen(false)}
              >
                <span className="material-icons">manage_accounts</span>
                Account settings
              </Link>

              <div className="theme-switch-wrapper">
                <span className="theme-icon material-icons">dark_mode</span>
                <label className="theme-switch">
                  <input
                    type="checkbox"
                    checked={theme === 'light'}
                    onChange={toggleTheme}
                  />
                  <span className="slider" />
                </label>
                <span className="theme-icon material-icons">light_mode</span>
              </div>

              <button
                className="navbar__dropdown-item logout"
                onClick={handleLogout}
                type="button"
              >
                <span className="material-icons">exit_to_app</span>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
